import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { AppLogo } from '../Layout/AppLogo';
import { ThemeToggle } from '../Layout/ThemeToggle';
import { supabase } from '../../lib/supabase';

interface PasswordResetProps {
  onBackToLogin: () => void;
}

export const PasswordReset: React.FC<PasswordResetProps> = ({ onBackToLogin }) => {
  const [token, setToken] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirm, setConfirm] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    
    const tokenParam = params.get('access_token') || hashParams.get('access_token');
    
    console.log('🔵 Auth: Password reset page loaded');
    console.log('🔵 Auth: Full URL:', window.location.href);
    console.log('🔵 Auth: Access token found:', !!tokenParam);
    
    setToken(tokenParam || '');
    if (!tokenParam) {
      setMessage('Missing access token.');
      console.error('🔴 Auth: No access token found in URL');
    } else {
      console.log('✅ Auth: Access token found and set');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token) {
      setMessage('Missing access token.');
      return;
    }
    
    if (password !== confirm) {
      setMessage('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setMessage('Password must be at least 6 characters long.');
      return;
    }

    setIsLoading(true);
    setMessage('');

    try {
      console.log('🔵 Updating password with Supabase');
      
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: token,
        refresh_token: '',
      });

      if (sessionError) {
        console.error('❌ Session error:', sessionError);
        setMessage(sessionError.message || 'Invalid or expired reset token.');
        setIsLoading(false);
        return;
      }
      
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        console.error('❌ Update error:', error);
        setMessage(error.message || 'Failed to update password.');
      } else {
        console.log('✅ Password updated successfully');
        setMessage('Your password has been updated!');
        setSuccess(true);
        setTimeout(() => {
          onBackToLogin();
        }, 3000);
      }
    } catch (err) {
      console.error('❌ Unexpected error:', err);
      setMessage('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-modular-shadow-quartz via-modular-graphite-smoke to-modular-frosted-indigo 
        dark:bg-dark-gradient flex items-center justify-center p-4">
        
        <div className="w-full max-w-md">
          <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-xl rounded-2xl 
            border border-modular-stone-alloy/30 dark:border-dark-border/50 p-8 shadow-lg text-center">
            
            <div className="flex justify-end items-center mb-4">
              <ThemeToggle />
            </div>
            
            <div className="flex items-center justify-center mb-6">
              <AppLogo size="lg" animated={false} />
              <div className="ml-4">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">PromptOS</h1>
              </div>
            </div>

            <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
            
            <h2 className="text-xl font-semibold text-emerald-600 dark:text-emerald-400 mb-3">
              Password Updated Successfully!
            </h2>
            
            <p className="text-gray-600 dark:text-dark-text-secondary mb-6">
              Your password has been successfully updated. You can now sign in with your new password.
            </p>
            
            <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 mb-4">
              <p className="text-emerald-700 dark:text-emerald-300 text-sm">
                Redirecting to login in 3 seconds...
              </p>
            </div>
            
            <button
              onClick={onBackToLogin}
              className="w-full bg-modular-primary-gradient text-white font-semibold py-3 px-6 rounded-lg 
                hover:shadow-modular-glow transition-all duration-300 flex items-center justify-center"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-modular-shadow-quartz via-modular-graphite-smoke to-modular-frosted-indigo 
      dark:bg-dark-gradient flex items-center justify-center p-4">
      
      <div className="w-full max-w-md">
        <div className="bg-white/95 dark:bg-dark-surface/95 backdrop-blur-xl rounded-2xl 
          border border-modular-stone-alloy/30 dark:border-dark-border/50 p-8 shadow-lg">
          
          <div className="flex justify-end items-center mb-4">
            <ThemeToggle />
          </div>
          
          <div className="flex items-center justify-center mb-6">
            <AppLogo size="lg" animated={false} />
            <div className="ml-4">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text">PromptOS</h1>
              <p className="text-lg text-modular-accent-blue dark:text-modular-accent-blue font-medium">
                AI Prompt Operating System
              </p>
            </div>
          </div>
          
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text mb-2">
              Reset Your Password
            </h2>
            <p className="text-gray-600 dark:text-dark-text-secondary text-sm">
              Enter your new password below
            </p>
          </div>

          {/* Show token status */}
          <div className="mb-6">
            {token ? (
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
                <p className="text-green-700 dark:text-green-400 text-sm">
                  ✅ Reset token verified. You can now set a new password.
                </p>
              </div>
            ) : (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5 mr-3" />
                  <div>
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                      Missing Access Token
                    </h3>
                    <p className="text-sm text-red-700 dark:text-red-400">
                      This password reset link is invalid or has expired. Please request a new password reset from the login page.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* New Password */}
            <div className="relative group">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 
                text-gray-400 dark:text-gray-500 group-focus-within:text-modular-accent-blue 
                dark:group-focus-within:text-modular-accent-blue transition-colors" />
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="New Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={!token || isLoading}
                className="w-full pl-10 pr-12 py-3 bg-gray-50 dark:bg-dark-card border 
                  border-gray-200 dark:border-dark-border rounded-lg text-gray-900 dark:text-dark-text 
                  placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 
                  focus:ring-modular-accent-blue dark:focus:ring-modular-accent-blue 
                  focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                required
                minLength={6}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                disabled={!token || isLoading}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 
                  hover:text-modular-accent-blue dark:hover:text-modular-accent-blue transition-colors duration-300
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            {/* Confirm Password */}
            <div className="relative group">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 
                text-gray-400 dark:text-gray-500 group-focus-within:text-modular-accent-blue 
                dark:group-focus-within:text-modular-accent-blue transition-colors" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm Password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={!token || isLoading}
                className="w-full pl-10 pr-12 py-3 bg-gray-50 dark:bg-dark-card border 
                  border-gray-200 dark:border-dark-border rounded-lg text-gray-900 dark:text-dark-text 
                  placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 
                  focus:ring-modular-accent-blue dark:focus:ring-modular-accent-blue 
                  focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                required
                minLength={6}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                disabled={!token || isLoading}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 
                  hover:text-modular-accent-blue dark:hover:text-modular-accent-blue transition-colors duration-300
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !password || !confirm || password !== confirm || !token}
              className="w-full bg-modular-primary-gradient text-white font-semibold 
                py-3 px-6 rounded-lg focus:outline-none focus:ring-2 focus:ring-modular-accent-blue 
                focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-dark-surface 
                transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed 
                shadow-modular-card hover:shadow-modular-glow transform hover:scale-[1.02] 
                active:scale-[0.98] flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Updating Password...
                </>
              ) : (
                <>
                  <Lock className="h-5 w-5 mr-2" />
                  {token ? 'Update Password' : 'Invalid Link'}
                </>
              )}
            </button>
          </form>

          {/* Message Display */}
          {message && (
            <div className={`mt-6 p-4 rounded-lg flex items-start space-x-3 ${
              message.includes('updated') || message.includes('success')
                ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
            }`}>
              {message.includes('updated') || message.includes('success') ? (
                <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              )}
              <div>
                <p className={`text-sm font-medium ${
                  message.includes('updated') || message.includes('success')
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-red-700 dark:text-red-300'
                }`}>
                  {message}
                </p>
              </div>
            </div>
          )}

          {/* Debug info in development */}
          {import.meta.env.DEV && (
            <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                <strong>Debug Info:</strong><br/>
                URL: {window.location.href}<br/>
                Access Token: {token ? 'Present' : 'Missing'}<br/>
                Token Length: {token.length}
              </p>
            </div>
          )}

          <div className="mt-6 text-center">
            <button
              onClick={onBackToLogin}
              className="text-modular-accent-blue dark:text-modular-accent-blue hover:text-blue-700 
                dark:hover:text-blue-300 font-medium transition-colors duration-300 hover:underline"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};